import supabase from '@/services/supabaseClient';
import { appointmentPaymentRoute } from '@/services/appointmentPaymentRouting.js';
import {
  filterAppointmentPaystackMethodsForPatient,
  loadAppointmentPaystackCapabilities,
} from "@/services/appointmentPaystackCapabilities.js";
import { loadAppointmentMtnMomoCapabilities } from "@/services/appointmentMtnMomoCapabilities.js";

function uniqueMethodIds(values) {
  return [...new Set((Array.isArray(values) ? values : []).map((value) => String(value || "").trim()).filter(Boolean))];
}

/**
 * Canonical appointment-payment capability authority.
 *
 * Provider functions declare what the active environment supports. Clinician
 * preferences narrow that list in doctorPaymentPreferences, and the Patient
 * runtime may narrow it once more for device-specific methods such as Apple
 * Pay. Every booking and clinician-setup surface must enter through this
 * resolver so supported methods cannot drift page by page.
 */
export async function loadSupportedAppointmentPaymentMethods({
  forClinicianSetup = false,
  serviceId = null,
  includeContext = false,
} = {}) {
  if (serviceId) {
    const route = await appointmentPaymentRoute(serviceId);
    if (route.selected) {
      if (!route.available) throw new Error('payment_capabilities_unavailable');
      const { data, error } = await supabase.functions.invoke('peach-appointment-checkout', { body: { action: 'capabilities', service_id: serviceId } });
      if (error || data?.error || !Array.isArray(data?.methods)) throw new Error('payment_capabilities_unavailable');
      const methods = uniqueMethodIds(data.methods);
      return includeContext ? { methods, authorizationOnly: true } : methods;
    }
  }
  const snapshot = await loadAppointmentPaystackCapabilities({ forClinicianSetup: true, includeEnvironment: true });
  // A server-issued Card-first scope deliberately excludes held rails. A failed
  // capability request never becomes an empty or fabricated provider snapshot.
  const mtnMomoMethods = snapshot.cardFirst ? [] : await loadAppointmentMtnMomoCapabilities();
  const methods = uniqueMethodIds([...snapshot.methods, ...mtnMomoMethods]);
  const available = forClinicianSetup ? methods : filterAppointmentPaystackMethodsForPatient(methods);
  return includeContext ? { methods: available, authorizationOnly: false } : available;
}

